import { useState, useEffect, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import {
  Pagination,
  PaginationContent,
  PaginationEllipsis,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
} from "@/components/ui/pagination";
import {
  MapPin,
  Building2,
  Bookmark,
  BookmarkX,
  Briefcase,
  DollarSign,
  ArrowLeft,
} from "lucide-react";

const JOBS_PER_PAGE = 20;

interface SavedJob {
  id: string;
  saved_at: string;
  job: {
    id: string;
    title: string;
    city: string | null;
    state: string | null;
    job_type: string[];
    salary_range_start: number | null;
    salary_range_end: number | null;
    created_at: string;
    is_confidential: boolean | null;
    company_profiles: {
      company_name: string;
      logo_url: string | null;
    };
  };
}

export default function CandidatoVagasSalvasPage() {
  const [savedJobs, setSavedJobs] = useState<SavedJob[]>([]);
  const [loading, setLoading] = useState(true);
  const [candidateProfileId, setCandidateProfileId] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const { toast } = useToast();
  const navigate = useNavigate();

  const totalPages = Math.ceil(totalCount / JOBS_PER_PAGE);

  const fetchSavedJobs = useCallback(async () => {
    if (!candidateProfileId) return;
    
    setLoading(true);

    const from = (currentPage - 1) * JOBS_PER_PAGE;
    const to = from + JOBS_PER_PAGE - 1;

    const { data, error, count } = await supabase
      .from("saved_jobs")
      .select(`
        id,
        created_at,
        jobs!inner (
          id,
          title,
          city,
          state,
          job_type,
          salary_range_start,
          salary_range_end,
          created_at,
          is_confidential,
          is_active,
          status,
          company_profiles (
            company_name,
            logo_url
          )
        )
      `, { count: 'exact' })
      .eq("candidate_id", candidateProfileId)
      .eq("jobs.is_active", true)
      .in("jobs.status", ["ativa", "pausada"])
      .order("created_at", { ascending: false })
      .range(from, to);

    if (error) {
      console.error("Error fetching saved jobs:", error);
      toast({
        title: "Erro",
        description: "Não foi possível carregar suas vagas salvas.",
        variant: "destructive",
      });
    } else {
      const formattedJobs = (data || []).map((item: any) => ({
        id: item.id,
        saved_at: item.created_at,
        job: {
          id: item.jobs.id,
          title: item.jobs.title,
          city: item.jobs.city,
          state: item.jobs.state,
          job_type: item.jobs.job_type,
          salary_range_start: item.jobs.salary_range_start,
          salary_range_end: item.jobs.salary_range_end,
          created_at: item.jobs.created_at,
          is_confidential: item.jobs.is_confidential,
          company_profiles: item.jobs.company_profiles,
        },
      }));
      setSavedJobs(formattedJobs);
      setTotalCount(count || 0);
    }

    setLoading(false);
  }, [candidateProfileId, currentPage, toast]);

  useEffect(() => {
    const checkAuth = async () => {
      const { data: { user } } = await supabase.auth.getUser();
      
      if (!user) {
        navigate("/entrar");
        return;
      }

      const { data: profile } = await supabase
        .from("candidate_profiles")
        .select("id")
        .eq("user_id", user.id)
        .maybeSingle();

      if (!profile) {
        toast({
          title: "Perfil necessário",
          description: "Você precisa ter um perfil de candidato para ver vagas salvas.",
          variant: "destructive",
        });
        navigate("/candidato/minha-conta");
        return;
      }

      setCandidateProfileId(profile.id);
    };

    checkAuth();
  }, [navigate, toast]);

  useEffect(() => {
    if (candidateProfileId) {
      fetchSavedJobs();
    }
  }, [candidateProfileId, fetchSavedJobs]);

  const handleRemoveSaved = async (savedJobId: string) => {
    const { error } = await supabase
      .from("saved_jobs")
      .delete()
      .eq("id", savedJobId);

    if (error) {
      console.error("Error removing saved job:", error);
      toast({
        title: "Erro",
        description: "Não foi possível remover a vaga dos salvos.",
        variant: "destructive",
      });
    } else {
      toast({
        title: "Vaga removida",
        description: "A vaga foi removida dos seus salvos.",
      });
      
      if (savedJobs.length === 1 && currentPage > 1) {
        setCurrentPage(currentPage - 1);
      } else {
        fetchSavedJobs();
      }
    }
  };

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const generatePageNumbers = (): (number | "ellipsis")[] => {
    const pages: (number | "ellipsis")[] = [];
    
    if (totalPages <= 7) {
      for (let i = 1; i <= totalPages; i++) pages.push(i);
    } else {
      pages.push(1);
      if (currentPage > 3) pages.push("ellipsis");
      for (let i = Math.max(2, currentPage - 1); i <= Math.min(totalPages - 1, currentPage + 1); i++) {
        pages.push(i);
      }
      if (currentPage < totalPages - 2) pages.push("ellipsis");
      pages.push(totalPages);
    }
    
    return pages;
  };

  const formatSalary = (start: number | null, end: number | null) => {
    if (!start && !end) return "A definir";
    if (start && end) {
      return `R$ ${start.toLocaleString("pt-BR")} - R$ ${end.toLocaleString("pt-BR")}`;
    }
    if (start) return `A partir de R$ ${start.toLocaleString("pt-BR")}`;
    return `Até R$ ${end?.toLocaleString("pt-BR")}`;
  };

  const formatLocation = (city: string | null, state: string | null) => {
    const parts = [city, state].filter(Boolean);
    return parts.length > 0 ? parts.join(", ") : "Não informado";
  };

  const getCompanyName = (job: SavedJob["job"]) => {
    if (job.is_confidential) return "Empresa Confidencial";
    return job.company_profiles?.company_name || "Empresa";
  };

  return (
    <div className="min-h-screen flex flex-col bg-background">
      <Header />

      <main className="flex-1">
        <div className="border-b border-border bg-card">
          <div className="container py-4">
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <Link to="/" className="hover:text-foreground transition-colors">
                Home
              </Link>
              <span>/</span>
              <span className="text-foreground">Vagas Salvas</span>
            </div>
          </div>
        </div>

        <div className="container py-8">
          <div className="mobile-container md:w-full">
            <Button variant="ghost" size="sm" className="mb-6" asChild>
              <Link to="/vagas" className="gap-2">
                <ArrowLeft className="h-4 w-4" />
                Voltar para vagas
              </Link>
            </Button>

            <div className="flex items-center gap-3 mb-6">
              <Bookmark className="h-6 w-6 text-primary" />
              <div>
                <h1 className="text-2xl font-display font-bold text-foreground">
                  Vagas Salvas
                </h1>
                {!loading && totalCount > 0 && (
                  <p className="text-sm text-muted-foreground">
                    Mostrando {((currentPage - 1) * JOBS_PER_PAGE) + 1}-{Math.min(currentPage * JOBS_PER_PAGE, totalCount)} de {totalCount} vaga{totalCount !== 1 ? "s" : ""}
                  </p>
                )}
              </div>
            </div>

            {loading ? (
              <div className="grid gap-4">
                {[1, 2, 3].map((i) => (
                  <Card key={i}>
                    <CardContent className="p-6">
                      <div className="flex gap-4">
                        <Skeleton className="h-12 w-12 rounded-lg" />
                        <div className="flex-1">
                          <Skeleton className="h-6 w-3/4 mb-2" />
                          <Skeleton className="h-4 w-1/2 mb-4" />
                          <div className="flex gap-4">
                            <Skeleton className="h-4 w-24" />
                            <Skeleton className="h-4 w-24" />
                          </div>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            ) : totalCount === 0 ? (
              <Card>
                <CardContent className="p-8 text-center">
                  <Bookmark className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                  <h2 className="text-lg font-semibold text-foreground mb-2">
                    Nenhuma vaga salva
                  </h2>
                  <p className="text-muted-foreground mb-4">
                    Você ainda não salvou nenhuma vaga. Explore as vagas disponíveis e salve as que mais interessam!
                  </p>
                  <Button asChild>
                    <Link to="/vagas">Explorar Vagas</Link>
                  </Button>
                </CardContent>
              </Card>
            ) : (
              <>
                <div className="grid gap-4">
                  {savedJobs.map((savedJob) => (
                    <Card key={savedJob.id} className="hover:shadow-md transition-shadow">
                      <CardContent className="p-4 md:p-6">
                        <div className="flex flex-col md:flex-row md:items-start justify-between gap-4">
                          <div className="flex gap-4 flex-1">
                            <div className="hidden md:flex h-12 w-12 rounded-lg bg-muted items-center justify-center flex-shrink-0">
                              {savedJob.job.company_profiles?.logo_url && !savedJob.job.is_confidential ? (
                                <img
                                  src={savedJob.job.company_profiles.logo_url}
                                  alt={savedJob.job.company_profiles.company_name}
                                  className="h-10 w-10 object-contain"
                                />
                              ) : (
                                <Building2 className="h-6 w-6 text-muted-foreground" />
                              )}
                            </div>

                            <div className="flex-1 min-w-0">
                              <Link
                                to={`/vagas/${savedJob.job.id}`}
                                className="text-lg font-semibold text-foreground hover:text-primary transition-colors block truncate"
                              >
                                {savedJob.job.title}
                              </Link>
                              <p className="text-muted-foreground text-sm mb-3">
                                {getCompanyName(savedJob.job)}
                              </p>

                              <div className="flex flex-wrap gap-3 text-sm text-muted-foreground">
                                <span className="flex items-center gap-1">
                                  <MapPin className="h-4 w-4" />
                                  <span className="md:hidden">{savedJob.job.city || "Não informado"}</span>
                                  <span className="hidden md:inline">{formatLocation(savedJob.job.city, savedJob.job.state)}</span>
                                </span>
                                <span className="hidden md:flex items-center gap-1">
                                  <Briefcase className="h-4 w-4" />
                                  {savedJob.job.job_type?.map(type => type.charAt(0).toUpperCase() + type.slice(1).toLowerCase()).join(", ") || "Não informado"}
                                </span>
                                <span className="hidden md:flex items-center gap-1">
                                  <DollarSign className="h-4 w-4" />
                                  {formatSalary(savedJob.job.salary_range_start, savedJob.job.salary_range_end)}
                                </span>
                              </div>
                            </div>
                          </div>

                          <div className="flex gap-2 md:flex-col md:items-end">
                            <Button asChild variant="default" size="sm">
                              <Link to={`/vagas/${savedJob.job.id}`}>Ver Vaga</Link>
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleRemoveSaved(savedJob.id)}
                              className="text-destructive hover:text-destructive hover:bg-destructive/10"
                            >
                              <BookmarkX className="h-4 w-4 mr-1" />
                              Remover
                            </Button>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>

                {totalCount > 0 && (
                  <Pagination className="mt-6">
                    <PaginationContent>
                      <PaginationItem>
                        <PaginationPrevious 
                          onClick={() => currentPage > 1 && handlePageChange(currentPage - 1)}
                          className={currentPage === 1 ? "pointer-events-none opacity-50" : "cursor-pointer"}
                        />
                      </PaginationItem>
                      
                      {generatePageNumbers().map((pageNum, idx) => (
                        pageNum === "ellipsis" ? (
                          <PaginationItem key={`ellipsis-${idx}`}>
                            <PaginationEllipsis />
                          </PaginationItem>
                        ) : (
                          <PaginationItem key={pageNum}>
                            <PaginationLink
                              onClick={() => handlePageChange(pageNum)}
                              isActive={currentPage === pageNum}
                              className="cursor-pointer"
                            >
                              {pageNum}
                            </PaginationLink>
                          </PaginationItem>
                        )
                      ))}
                      
                      <PaginationItem>
                        <PaginationNext 
                          onClick={() => currentPage < totalPages && handlePageChange(currentPage + 1)}
                          className={currentPage === totalPages || totalPages === 0 ? "pointer-events-none opacity-50" : "cursor-pointer"}
                        />
                      </PaginationItem>
                    </PaginationContent>
                  </Pagination>
                )}
              </>
            )}
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
}
